// Copyright © 2019-2026 Focal Systems. All rights reserved.

export type DayPart = "morning" | "afternoon" | "evening" | "night";

/** 05–11 morning, 12–16 afternoon, 17–21 evening, otherwise night. */
export function dayPart(hour: number): DayPart {
  if (hour >= 5 && hour < 12) return "morning";
  if (hour >= 12 && hour < 17) return "afternoon";
  if (hour >= 17 && hour < 22) return "evening";
  return "night";
}

/** Greeting keys under `home.greeting`, each taking a `{{name}}`. */
const DAY_PART_GREETINGS: Record<DayPart, readonly string[]> = {
  morning: ["morning.plain", "morning.coffee", "morning.riseAndShine"],
  afternoon: ["afternoon.plain", "afternoon.halfway", "afternoon.lunch"],
  evening: ["evening.plain", "evening.wrappingUp", "evening.stillAtIt"],
  night: ["night.midnightOil", "night.lateOne", "night.quietHours"],
};

/** Extras mixed into the pool on particular days, so a Monday morning can say so. */
function weekdayGreetings(weekday: number, part: DayPart): string[] {
  if (weekday === 0 || weekday === 6) return ["weekday.weekend"];
  if (weekday === 1 && part === "morning") return ["weekday.monday"];
  if (weekday === 5 && (part === "afternoon" || part === "evening")) return ["weekday.friday"];
  return [];
}

/** Every key `pickGreeting` can return, for checking the locales carry them all. */
export const ALL_GREETING_KEYS = [
  ...Object.values(DAY_PART_GREETINGS).flat(),
  "weekday.weekend",
  "weekday.monday",
  "weekday.friday",
];

/** A random greeting key (under `home.greeting`) that fits the local time and weekday of `now`. */
export function pickGreeting(now: Date, random: () => number = Math.random): string {
  const part = dayPart(now.getHours());
  const pool = [...DAY_PART_GREETINGS[part], ...weekdayGreetings(now.getDay(), part)];
  return pool[Math.floor(random() * pool.length)] ?? DAY_PART_GREETINGS[part][0] ?? "morning.plain";
}

/** The name to greet: the first word of the full name, else the email's local part. */
export function greetingName(fullName: string | undefined, email: string | undefined): string {
  const first = fullName?.trim().split(/\s+/)[0];
  if (first) return first;
  return email?.split("@")[0] ?? "";
}
